import { useNavigate, useParams } from "react-router-dom";
import AdminOnly from "#/components/auth/AdminOnly";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import ArticleOutlineDetail from "#/admin/article-outlines/components/ArticleOutlineDetail";
import useArticleOutlineDetail from "#/admin/article-outlines/hooks/useArticleOutlineDetail";
import useArticleOutlineMutations from "#/admin/article-outlines/hooks/useArticleOutlineMutations";
import { useToast } from "#/hooks/ToastContext";

export default function AdminArticleOutlineDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detailQuery = useArticleOutlineDetail(id);
  const { approveOutline, regenerateOutline, removeOutline } =
    useArticleOutlineMutations();
  const { showToast } = useToast();

  const outlineTitle =
    detailQuery.data?.title || detailQuery.data?.name || "Article outline";
  const hasOutlineContent = Boolean(
    detailQuery.data?.outline ||
      (Array.isArray(detailQuery.data?.bullets) &&
        detailQuery.data.bullets.length > 0)
  );
  const isApproved = Boolean(detailQuery.data?.approved_at);
  const isBusy =
    approveOutline.isPending ||
    regenerateOutline.isPending ||
    removeOutline.isPending;

  if (!id) {
    return (
      <AdminOnly>
        <div className="container mx-auto px-4 py-6">
          <Typography variant="h4" className="text-terracotta">
            Outline not found
          </Typography>
        </div>
      </AdminOnly>
    );
  }

  return (
    <AdminOnly>
      <div className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Typography variant="caption" className="text-brown-500">
              Article outlines / {outlineTitle}
            </Typography>
            <Typography variant="h2" className="text-brown-900">
              {outlineTitle}
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Review and edit the full outline content.
            </Typography>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Approve — only enabled when outline has content and isn't already approved */}
            <ActionButton
              onClick={async () => {
                await approveOutline.mutateAsync({ id, generateNow: true });
                showToast("Outline approved. Article generation started.", "success");
                navigate("/admin/content/outlines", {
                  state: { pendingOutlineId: id },
                });
              }}
              isLoading={approveOutline.isPending}
              disabled={isBusy || !hasOutlineContent || isApproved}
              title={
                !hasOutlineContent
                  ? "Add outline content before approving"
                  : isApproved
                    ? "Already approved"
                    : undefined
              }
            >
              Approve &amp; generate article
            </ActionButton>

            {/* Regenerate outline */}
            <ActionButton
              tone="secondary"
              onClick={async () => {
                const confirmed = window.confirm(
                  `Regenerate outline for "${outlineTitle}"?\n\nThis will clear the current bullets and outline content and re-queue it for the pipeline runner.`
                );
                if (!confirmed) return;
                await regenerateOutline.mutateAsync(id);
                showToast("Outline reset and queued for regeneration.", "success");
                detailQuery.refetch();
              }}
              isLoading={regenerateOutline.isPending}
              disabled={isBusy}
            >
              Regenerate outline
            </ActionButton>

            {/* Delete */}
            <ActionButton
              tone="danger"
              onClick={async () => {
                const confirmed = window.confirm(
                  `Permanently delete "${outlineTitle}"?\n\nThe medical concept will be kept so a new outline can be generated.\n\nThis cannot be undone.`
                );
                if (!confirmed) return;
                await removeOutline.mutateAsync(id);
                navigate("/admin/content/outlines");
              }}
              isLoading={removeOutline.isPending}
              disabled={isBusy}
            >
              Delete outline
            </ActionButton>

            <ActionButton
              tone="secondary"
              onClick={() => navigate("/admin/content/outlines")}
            >
              Back to outlines
            </ActionButton>
          </div>
        </div>

        <ArticleOutlineDetail id={id} />
      </div>
    </AdminOnly>
  );
}
